import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { type PhotoWindow, type PhotoWindows, STATE_TONE, mytWhen } from './photoWindows.js';

function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

/** "Pickup photos ✓", "Return photos from 9 Oct, 9:30 am", "Pickup photos not sent". */
export function photoTagText(word: 'Pickup' | 'Return', w: PhotoWindow): string {
  switch (w.state) {
    case 'sent':
      return `${word} photos ✓`;
    case 'open':
      return `${word} photos open until ${mytWhen(w.closes_at)}`;
    case 'later':
      return `${word} photos from ${mytWhen(w.opens_at)}`;
    case 'missed':
      return `${word} photos not sent`;
    default:
      return `${word} photos not available`;
  }
}

/**
 * 374 (US6): what staff need before scrolling — status, car and plate, the rental's pickup → return, the customer,
 * and the photo state. Each part is shown only when the booking carries it.
 */
export function BookingSummaryLine({
  status,
  car,
  pickupAt,
  returnAt,
  customer,
  photoWindows,
}: {
  status: string;
  car?: { label: string; plate_number: string } | null;
  pickupAt: string;
  returnAt: string;
  customer?: { id: string; name: string } | null;
  photoWindows?: PhotoWindows;
}) {
  return (
    <Card padding="sm" data-testid="booking-summary">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <StatusBadge status={status} />
        {car && (
          <span>
            <span className="font-semibold text-ink">{car.label}</span>{' '}
            <span className="font-mono text-xs text-steel">{car.plate_number}</span>
          </span>
        )}
        <span data-testid="booking-summary-times" className="text-ink">
          {formatTime(pickupAt)} → {formatTime(returnAt)}
        </span>
        {customer && (
          <Link to={`/users/${customer.id}`} className="text-primary hover:underline">
            {customer.name}
          </Link>
        )}
        {photoWindows && (
          <span className="flex flex-wrap gap-1.5">
            {(['pickup', 'return'] as const).map((m) => (
              <a
                key={m}
                href="#car-condition"
                data-testid={`summary-photos-${m}`}
                className={`rounded-md px-2 py-0.5 text-xs font-bold ${STATE_TONE[photoWindows[m].state]}`}
              >
                {photoTagText(m === 'pickup' ? 'Pickup' : 'Return', photoWindows[m])}
              </a>
            ))}
          </span>
        )}
      </div>
    </Card>
  );
}
